import { memo, useCallback, useId, type ReactNode } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { DialogCloseButton } from '../DialogCloseButton.js';
import styles from './FullScreenDialog.module.css';

export interface FullScreenDialogProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly children: ReactNode;
  /** Visible dialog title (wired to `aria-labelledby`). */
  readonly title: string;
  /** Optional supporting copy (wired to `aria-describedby` when provided). */
  readonly description?: string;
  /**
   * When true, Escape / backdrop / close are blocked (busy-dismiss matrix for save/delete).
   */
  readonly isBusy?: boolean;
  /** Full-bleed surface has no outside chrome; default false. */
  readonly closeOnOverlayClick?: boolean;
  /** Sticky action bar (Spec sticky allowlist: modal footer). */
  readonly footer?: ReactNode;
  readonly testId?: string;
}

export const FullScreenDialog = memo<FullScreenDialogProps>(({
  isOpen,
  onClose,
  children,
  title,
  description,
  isBusy = false,
  closeOnOverlayClick = false,
  footer,
  testId = 'full-screen-dialog',
}) => {
  const reactId = useId();
  const titleId = `${testId}-title-${reactId}`;
  const descriptionId = `${testId}-description-${reactId}`;

  const handleOpenChange = useCallback((open: boolean): void => {
    if (!open) {
      if (isBusy) {
        return;
      }
      onClose();
    }
  }, [isBusy, onClose]);

  const handleInteractOutside = useCallback((event: Event): void => {
    if (isBusy || !closeOnOverlayClick) {
      event.preventDefault();
    }
  }, [closeOnOverlayClick, isBusy]);

  const handleEscapeKeyDown = useCallback((event: KeyboardEvent): void => {
    if (isBusy) {
      event.preventDefault();
    }
  }, [isBusy]);

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={styles.overlay}
          data-testid={`${testId}-overlay`}
        />
        <Dialog.Content
          className={styles.content}
          data-testid={testId}
          aria-labelledby={titleId}
          aria-describedby={description !== undefined ? descriptionId : undefined}
          onPointerDownOutside={handleInteractOutside}
          onInteractOutside={handleInteractOutside}
          onEscapeKeyDown={handleEscapeKeyDown}
        >
          <header className={styles.header}>
            <Dialog.Title asChild>
              <h2 id={titleId} className={styles.title}>{title}</h2>
            </Dialog.Title>
            <Dialog.Close asChild>
              <DialogCloseButton testId={`${testId}-close`} disabled={isBusy} />
            </Dialog.Close>
          </header>
          {description !== undefined ? (
            <Dialog.Description id={descriptionId} className={styles.description}>
              {description}
            </Dialog.Description>
          ) : null}
          <div className={`admin-scrollbar ${styles.body}`}>{children}</div>
          {footer !== undefined ? (
            <footer className={styles.footer}>{footer}</footer>
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
});

FullScreenDialog.displayName = 'FullScreenDialog';
